import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * The phone's Back closes a mode on the screen first, as an open sheet does (components/Overlay.tsx) and as
 * WhatsApp does (mobile review, 19 Oct: Back while searching or selecting chats left the list). While `active`,
 * the screen has one extra history step on the same page, marked with `key`; Back takes it away and `close` runs.
 * Closing in the app takes the step away too. A step a mode left behind (the screen moved on, or a sheet sat on
 * top of it) is passed over the next time Back lands on it.
 */
const backPending = new Set<string>();

function stateOf(): Record<string, unknown> | null {
  return window.history.state as Record<string, unknown> | null;
}

export function useBackStep(key: string, active: boolean, close: () => void) {
  const activeRef = useRef(active);
  activeRef.current = active;
  const closeRef = useRef(close);
  closeRef.current = close;

  // Before the tap that changed the mode ends, so it is in place before a closing sheet takes its own step.
  useLayoutEffect(() => {
    const state = stateOf();
    if (active) {
      if (state?.chatsSheet) {
        // Started from a sheet (More, then Select): the sheet's step becomes this one, so closing the sheet
        // does not take it away.
        const { chatsSheet: _sheet, ...rest } = state;
        window.history.replaceState({ ...rest, [key]: true }, '');
      } else if (!state?.[key]) window.history.pushState({ ...(state ?? {}), [key]: true }, '');
    } else if (state?.[key] && !state.chatsSheet && !backPending.has(key)) {
      // A sheet on top takes its own step first; the landing on this one passes over it (below).
      backPending.add(key);
      window.history.back();
    }
  }, [key, active]);

  useEffect(() => {
    const onBack = () => {
      if (backPending.delete(key)) return;
      const state = stateOf();
      if (activeRef.current && !state?.[key]) closeRef.current();
      else if (!activeRef.current && state?.[key] && !state.chatsSheet) {
        backPending.add(key);
        window.history.back();
      }
    };
    window.addEventListener('popstate', onBack);
    return () => window.removeEventListener('popstate', onBack);
  }, [key]);
}
